<template>
  <SectionListIcon024
    :title="t('title')"
    :number-of-line="4"
    :items="items"
    theme="cyan"
  />
</template>

<script setup lang="ts">
  import type { IItem } from '@/components/Shared/Section/List/Icon024.vue';

  const { t } = useI18n({ useScope: 'local' });
  const { blobDomain } = useWebsite();

  const provideIcon = (index: number): string =>
    `${blobDomain.value}/wise-paas-marketplace/topics2/doe/feature-${index}.webp`;

  const items = computed((): IItem[] =>
    [1, 2, 3, 4].map((index) => ({
      name: t(`item-${index}-title`),
      description: t(`item-${index}-description`),
      thumbnail: provideIcon(index),
    }))
  );
</script>

<i18n lang="json">
{
  "en-int": {
    "title": "What Modern Digital O&M Requires",
    "item-1-title": "Hardware–Software Integration",
    "item-1-description": "Align devices and software to enable secure connectivity and consistent data flows across sites.",
    "item-2-title": "Ready-to-Use Applications",
    "item-2-description": "Deploy ready-made applications for device integration and remote O&M, enabling faster rollout with minimal setup effort.",
    "item-3-title": "Scalable Multi-Site Operations",
    "item-3-description": "Support standardized workflows and dashboards to improve visibility as environments scale.",
    "item-4-title": "AI-Ready Data Architecture",
    "item-4-description": "Structure operational and energy data to support predictive maintenance and data-driven optimization."
  },
  "en-jp": {
    "title": "What Modern Digital O&M Requires",
    "item-1-title": "Hardware–Software Integration",
    "item-1-description": "Align devices and software to enable secure connectivity and consistent data flows across sites.",
    "item-2-title": "Ready-to-Use Applications",
    "item-2-description": "Deploy ready-made applications for device integration and remote O&M, enabling faster rollout with minimal setup effort.",
    "item-3-title": "Scalable Multi-Site Operations",
    "item-3-description": "Support standardized workflows and dashboards to improve visibility as environments scale.",
    "item-4-title": "AI-Ready Data Architecture",
    "item-4-description": "Structure operational and energy data to support predictive maintenance and data-driven optimization."
  },
  "en-tw": {
    "title": "What Modern Digital O&M Requires",
    "item-1-title": "Hardware–Software Integration",
    "item-1-description": "Align devices and software to enable secure connectivity and consistent data flows across sites.",
    "item-2-title": "Ready-to-Use Applications",
    "item-2-description": "Deploy ready-made applications for device integration and remote O&M, enabling faster rollout with minimal setup effort.",
    "item-3-title": "Scalable Multi-Site Operations",
    "item-3-description": "Support standardized workflows and dashboards to improve visibility as environments scale.",
    "item-4-title": "AI-Ready Data Architecture",
    "item-4-description": "Structure operational and energy data to support predictive maintenance and data-driven optimization."
  },
  "en-kr": {
    "title": "What Modern Digital O&M Requires",
    "item-1-title": "Hardware–Software Integration",
    "item-1-description": "Align devices and software to enable secure connectivity and consistent data flows across sites.",
    "item-2-title": "Ready-to-Use Applications",
    "item-2-description": "Deploy ready-made applications for device integration and remote O&M, enabling faster rollout with minimal setup effort.",
    "item-3-title": "Scalable Multi-Site Operations",
    "item-3-description": "Support standardized workflows and dashboards to improve visibility as environments scale.",
    "item-4-title": "AI-Ready Data Architecture",
    "item-4-description": "Structure operational and energy data to support predictive maintenance and data-driven optimization."
  },
  "en-americas": {
    "title": "What Modern Digital O&M Requires",
    "item-1-title": "Hardware–Software Integration",
    "item-1-description": "Align devices and software to enable secure connectivity and consistent data flows across sites.",
    "item-2-title": "Ready-to-Use Applications",
    "item-2-description": "Deploy ready-made applications for device integration and remote O&M, enabling faster rollout with minimal setup effort.",
    "item-3-title": "Scalable Multi-Site Operations",
    "item-3-description": "Support standardized workflows and dashboards to improve visibility as environments scale.",
    "item-4-title": "AI-Ready Data Architecture",
    "item-4-description": "Structure operational and energy data to support predictive maintenance and data-driven optimization."
  },
  "en-asia-pacific": {
    "title": "What Modern Digital O&M Requires",
    "item-1-title": "Hardware–Software Integration",
    "item-1-description": "Align devices and software to enable secure connectivity and consistent data flows across sites.",
    "item-2-title": "Ready-to-Use Applications",
    "item-2-description": "Deploy ready-made applications for device integration and remote O&M, enabling faster rollout with minimal setup effort.",
    "item-3-title": "Scalable Multi-Site Operations",
    "item-3-description": "Support standardized workflows and dashboards to improve visibility as environments scale.",
    "item-4-title": "AI-Ready Data Architecture",
    "item-4-description": "Structure operational and energy data to support predictive maintenance and data-driven optimization."
  },
  "en-emea": {
    "title": "What Modern Digital O&M Requires",
    "item-1-title": "Hardware–Software Integration",
    "item-1-description": "Align devices and software to enable secure connectivity and consistent data flows across sites.",
    "item-2-title": "Ready-to-Use Applications",
    "item-2-description": "Deploy ready-made applications for device integration and remote O&M, enabling faster rollout with minimal setup effort.",
    "item-3-title": "Scalable Multi-Site Operations",
    "item-3-description": "Support standardized workflows and dashboards to improve visibility as environments scale.",
    "item-4-title": "AI-Ready Data Architecture",
    "item-4-description": "Structure operational and energy data to support predictive maintenance and data-driven optimization."
  },
  "ja-jp": {
    "title": "デジタルO&Mの主要能力",
    "item-1-title": "ハードウェアとソフトウェアの統合",
    "item-1-description": "デバイスとソフトウェアを連携し、セキュアな接続と一貫したデータフローを実現します。",
    "item-2-title": "すぐに使えるアプリケーション",
    "item-2-description": "デバイス統合と遠隔O&M向けの既製アプリケーションを活用し、最小限のセットアップで迅速な導入を実現します。",
    "item-3-title": "スケーラブルなマルチサイト運用",
    "item-3-description": "標準化されたワークフローとダッシュボードにより、拠点拡大時も一貫した可視性を維持します。",
    "item-4-title": "AI 活用を前提としたデータ基盤",
    "item-4-description": "運用・エネルギーデータを構造化し、予知保全やデータドリブン最適化を支援します。"
  },
  "zh-tw": {
    "title": "現代數位維運的關鍵能力",
    "item-1-title": "軟硬體整合能力",
    "item-1-description": "整合設備與軟體，確保安全連線與穩定資料流",
    "item-2-title": "即用型工業App",
    "item-2-description": "提供預先整合的應用，最小部署成本快速上線",
    "item-3-title": "可擴展的多據點營運",
    "item-3-description": "透過標準化流程與儀表板，支援規模化集中管理",
    "item-4-title": "AI-ready 數據架構",
    "item-4-description": "以結構化數據，支援預測性維護與數據驅動優化"
  }
}
</i18n>
